'use client';

import { useEffect } from 'react';

const PerformanceMonitor = () => {
  useEffect(() => {
    // Only run in browser
    if (typeof window === 'undefined') return;

    // Monitor Core Web Vitals
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        // Log performance metrics
        console.log(`${entry.name}: ${entry.value}`);
        
        // Send to analytics if available
        if (window.gtag) {
          window.gtag('event', entry.name, {
            value: Math.round(entry.value),
            event_category: 'Web Vitals',
            event_label: entry.id,
            non_interaction: true,
          });
        }
      }
    });

    // Observe Core Web Vitals
    observer.observe({ entryTypes: ['largest-contentful-paint', 'first-input', 'layout-shift'] });

    // Monitor page load performance
    window.addEventListener('load', () => {
      const navigation = performance.getEntriesByType('navigation')[0];
      const paint = performance.getEntriesByType('paint');
      
      const metrics = {
        dns: navigation.domainLookupEnd - navigation.domainLookupStart,
        tcp: navigation.connectEnd - navigation.connectStart,
        ttfb: navigation.responseStart - navigation.requestStart,
        domContentLoaded: navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart,
        loadComplete: navigation.loadEventEnd - navigation.loadEventStart,
        firstPaint: paint.find(entry => entry.name === 'first-paint')?.startTime,
        firstContentfulPaint: paint.find(entry => entry.name === 'first-contentful-paint')?.startTime,
      };

      console.log('Page Load Metrics:', metrics);
      
      // Send to analytics
      if (window.gtag) {
        window.gtag('event', 'page_load_metrics', {
          custom_map: {
            dns_time: 'dns',
            tcp_time: 'tcp',
            ttfb: 'ttfb',
            dom_content_loaded: 'domContentLoaded',
            load_complete: 'loadComplete',
            first_paint: 'firstPaint',
            first_contentful_paint: 'firstContentfulPaint',
          },
          dns_time: Math.round(metrics.dns),
          tcp_time: Math.round(metrics.tcp),
          ttfb: Math.round(metrics.ttfb),
          dom_content_loaded: Math.round(metrics.domContentLoaded),
          load_complete: Math.round(metrics.loadComplete),
          first_paint: Math.round(metrics.firstPaint || 0),
          first_contentful_paint: Math.round(metrics.firstContentfulPaint || 0),
        });
      }
    });

    // Monitor resource loading
    const resourceObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.initiatorType === 'img' && entry.duration > 1000) {
          console.warn('Slow image load:', entry.name, entry.duration);
        }
      }
    });

    resourceObserver.observe({ entryTypes: ['resource'] });

    // Monitor errors
    window.addEventListener('error', (event) => {
      console.error('Page Error:', event.error);
      
      if (window.gtag) {
        window.gtag('event', 'exception', {
          description: event.error?.message || 'Unknown error',
          fatal: false,
        });
      }
    });

    // Monitor unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      console.error('Unhandled Promise Rejection:', event.reason);
      
      if (window.gtag) {
        window.gtag('event', 'exception', {
          description: event.reason?.message || 'Unhandled promise rejection',
          fatal: false,
        });
      }
    });

    return () => {
      observer.disconnect();
      resourceObserver.disconnect();
    };
  }, []);

  return null; // This component doesn't render anything
};

export default PerformanceMonitor;
